import { useCallback } from "react";

import { cn } from "@btnet/utilities-core";

import { BdsIconEnvelopeSimple, BdsIconHouse, BdsIconInfoCircle } from "@buildertrend/components";

import { useTracking } from "utilities/analytics/analytics";
import { jobDetailRoute } from "utilities/flatRoutes/job.routes";
import { isSpecialJob } from "utilities/jobPicker/jobPicker";
import { routes } from "utilities/routes";

import { BTLink, BTLinkRelative } from "commonComponents/btWrappers/BTLink/BTLink";
import { BTPopover } from "commonComponents/btWrappers/BTPopover/BTPopover";
import {
    type JobPickerJob,
    type JobPickerOwnerInfo,
    type JobPickerPermissions,
} from "commonComponents/utilities/JobPicker/JobPicker.api.types";
import {
    getStatusTextForJob,
    getStatusTypeForJob,
} from "commonComponents/utilities/JobPicker/JobPicker.utils";
import {
    ALL_JOBS,
    GLOBAL_JOB,
} from "commonComponents/utilities/JobPicker/Sections/JobsList/JobsList.types";
import { ShowOnPortal } from "commonComponents/utilities/ShowOnPortal/ShowOnPortal";
import { StatusTagDisplay } from "commonComponents/utilities/Status/StatusTagDisplay";
import TextTruncate from "commonComponents/utilities/TextTruncate/TextTruncate";

import { JobStatus } from "entity/job/Job.api.types";

interface IJobInfoIconProps {
    canViewJobDetails?: boolean;
    currentJobId: number;
    currentJobStatus: JobStatus;
    templatesOnly?: boolean;
}

function JobInfoIcon({
    canViewJobDetails,
    currentJobId,
    currentJobStatus,
    templatesOnly,
}: IJobInfoIconProps) {
    if (!canViewJobDetails || isSpecialJob(currentJobId, currentJobStatus)) {
        return null;
    }

    const getJobFromTemplateLink = () => {
        if (templatesOnly) {
            return "/JobPickerActions" + routes.template.getTemplateDetailsLink(currentJobId);
        }
        return "/JobPickerActions" + routes.job.getDetailsLink(currentJobId);
    };

    return (
        <BTPopover content={`View ${templatesOnly ? "template" : "job"} details`}>
            {!templatesOnly ? (
                <BTLink to={jobDetailRoute.link({ jobId: currentJobId })}>
                    <BdsIconInfoCircle testid="JobInfoIcon" />
                </BTLink>
            ) : (
                <BTLinkRelative to={getJobFromTemplateLink()}>
                    <BdsIconInfoCircle testid="JobInfoIcon" />
                </BTLinkRelative>
            )}
        </BTPopover>
    );
}

interface IMessageIconProps {
    currentJob: JobPickerJob;
    permissions?: JobPickerPermissions;
    templatesOnly?: boolean;
    ownerInfo?: JobPickerOwnerInfo;
}

function MessageIcon({ currentJob, ownerInfo, permissions, templatesOnly }: IMessageIconProps) {
    const isAnyOwnerActive = ownerInfo?.owners.some((x) => x.isActive);
    const showMessageIcon = useCallback(
        (jobId: number, jobStatus: JobStatus) =>
            !isSpecialJob(jobId, jobStatus) && permissions?.canSendMessage && !templatesOnly,
        [permissions?.canSendMessage, templatesOnly],
    );

    if (!showMessageIcon(currentJob.jobId, currentJob.jobStatus)) {
        return null;
    }

    return (
        <BTPopover
            content={
                isAnyOwnerActive
                    ? "Send message to client"
                    : "No active contacts on this job. Only active contacts on this job can be messaged."
            }
        >
            {isAnyOwnerActive ? (
                <BTLinkRelative
                    to={
                        "/JobPickerActions" +
                        routes.messages.getComposeLink(0, currentJob.jobId, {
                            defaultRecipientId: currentJob.jobId,
                        })
                    }
                >
                    <BdsIconEnvelopeSimple testid="SendMessageIcon" />
                </BTLinkRelative>
            ) : (
                <BdsIconEnvelopeSimple testid="SendMessageIcon" className="opacity-50" />
            )}
        </BTPopover>
    );
}

interface IOwnerIconProps {
    currentJob: JobPickerJob;
    permissions?: JobPickerPermissions;
    templatesOnly?: boolean;
}

function OwnerIcon({ currentJob, permissions, templatesOnly }: IOwnerIconProps) {
    if (
        !permissions?.canViewOwnerSite ||
        isSpecialJob(currentJob.jobId, currentJob.jobStatus) ||
        templatesOnly
    ) {
        return null;
    }

    return (
        <BTPopover
            content={
                currentJob.canImpersonateOwner
                    ? "View client portal"
                    : "Add a client contact to the job to view client site"
            }
        >
            {currentJob.canImpersonateOwner ? (
                <>
                    {/* we need the opener on the client portal because the main nav has a "close" option that attempts to close the window/tab */}
                    {/* eslint-disable-next-line react/jsx-no-target-blank */}
                    <a
                        target="_blank"
                        rel="opener"
                        href={`/app${routes.getOwnerPortalRedirectLink(currentJob.jobId)}`}
                    >
                        <BdsIconHouse testid="OwnerPortalIcon" />
                    </a>
                </>
            ) : (
                <BdsIconHouse testid="OwnerPortalIcon" className="opacity-50" />
            )}
        </BTPopover>
    );
}

interface IJobInfoProps {
    currentJob: JobPickerJob;
    permissions?: JobPickerPermissions;
    ownerInfo?: JobPickerOwnerInfo;
    templatesOnly?: boolean;
    isTemplateMode: boolean;
    isPopover?: boolean;
}

export function JobInfo({
    currentJob,
    isTemplateMode,
    ownerInfo,
    permissions,
    templatesOnly,
    isPopover,
}: IJobInfoProps) {
    const { Track } = useTracking({
        jobId: currentJob.jobId,
        component: "JobPicker",
    });
    const isUK = currentJob.jobInfo.globalCountry && currentJob.jobInfo.globalCountry === "GB";

    return (
        <Track>
            <div
                data-testid={isPopover ? "JobInformationContainer" : "CurrentJobInformation"}
                className={cn("flex flex-col bg-gray-0 w-full", {
                    "rounded-md border border-default shadow-raised pt-2 px-4 pb-2": !isPopover,
                })}
                style={{
                    borderLeft: isPopover
                        ? undefined
                        : `var(--bui-token-spacing-2) solid ${currentJob.jobInfo.color}`,
                }}
            >
                <div
                    className={cn("py-2 flex flex-row items-center w-full", {
                        "justify-end": !(!isTemplateMode && currentJob.jobId > 0),
                        "justify-between": !isTemplateMode && currentJob.jobId > 0,
                    })}
                >
                    {!isTemplateMode && currentJob.jobId > 0 && (
                        <StatusTagDisplay
                            useBds
                            statusType={getStatusTypeForJob(currentJob.jobInfo.status)}
                            statusText={getStatusTextForJob(currentJob.jobInfo.status)}
                        />
                    )}

                    <ShowOnPortal
                        builder
                        subs
                        render={() => (
                            <div className=" flex flex-row items-center justify-end gap-2">
                                <JobInfoIcon
                                    canViewJobDetails={permissions?.canViewJobDetails}
                                    currentJobId={currentJob.jobId}
                                    currentJobStatus={currentJob.jobStatus}
                                    templatesOnly={templatesOnly}
                                />
                                <MessageIcon
                                    currentJob={currentJob}
                                    permissions={permissions}
                                    templatesOnly={templatesOnly}
                                    ownerInfo={ownerInfo}
                                />
                                <OwnerIcon
                                    currentJob={currentJob}
                                    permissions={permissions}
                                    templatesOnly={templatesOnly}
                                />
                            </div>
                        )}
                    />
                </div>
                <div className="text-lg font-semibold font-sans flex flex-row items-center w-full overflow-hidden text-ellipsis text-gray-90">
                    <TextTruncate>{currentJob.jobName}</TextTruncate>
                </div>

                {currentJob.jobInfo.ownerName !== null && currentJob.jobInfo.ownerName !== "" && (
                    <div className="text-sm font-normal font-sans">
                        {currentJob.jobInfo.ownerName}
                    </div>
                )}
                {currentJob.jobInfo.street !== null && currentJob.jobInfo.street !== "" && (
                    <div className="text-sm font-normal font-sans">{currentJob.jobInfo.street}</div>
                )}
                {isUK && (
                    <>
                        {currentJob.jobInfo.city && (
                            <div className="text-sm font-normal font-sans">
                                {currentJob.jobInfo.city}
                            </div>
                        )}
                        {currentJob.jobInfo.suburb && (
                            <div className="text-sm font-normal font-sans">
                                {currentJob.jobInfo.suburb}
                            </div>
                        )}
                        {currentJob.jobInfo.zip && (
                            <div className="text-sm font-normal font-sans">
                                {currentJob.jobInfo.zip}
                            </div>
                        )}
                    </>
                )}
                {!isUK &&
                    currentJob.jobInfo.addressWithoutStreet !== null &&
                    currentJob.jobInfo.addressWithoutStreet !== "" && (
                        <div className="text-sm font-normal font-sans">
                            {currentJob.jobInfo.addressWithoutStreet}
                        </div>
                    )}
            </div>
        </Track>
    );
}

interface ISpecialJobInfoProps {
    currentJob?: JobPickerJob;
    isTimeClock: boolean;
    isGeneral: boolean;
}

function SpecialJobInfo(props: ISpecialJobInfoProps) {
    const { currentJob, isTimeClock, isGeneral } = props;
    return (
        <div
            className="rounded-md border border-default shadow-raised flex flex-col py-2 px-4 bg-gray-0"
            data-testid="TimeClockGeneralJobInformation"
        >
            {currentJob?.jobName && (
                <div className="text-lg font-semibold font-sans flex flex-row items-center w-full overflow-hidden text-ellipsis text-gray-90">
                    <TextTruncate>{currentJob?.jobName}</TextTruncate>
                </div>
            )}
            {isTimeClock && (
                <div className="text-sm font-normal font-sans">
                    Allows you to clock in/out without assigning that time to a specific job
                </div>
            )}
            {isGeneral && (
                <div className="text-sm font-normal font-sans">
                    Manage items that aren't assigned to a specific job
                </div>
            )}
        </div>
    );
}

export interface IJobPickerJobInfoSectionProps {
    selectedJobIds: number[] | number | null;
    jobs: JobPickerJob[];
    permissions: JobPickerPermissions;
    ownerInfo: JobPickerOwnerInfo;
    templatesOnly: boolean;
    isTemplateMode: boolean;
}

export function JobInfoSection({
    selectedJobIds,
    jobs,
    permissions,
    ownerInfo,
    templatesOnly,
    isTemplateMode,
}: IJobPickerJobInfoSectionProps) {
    if (typeof selectedJobIds !== "number") {
        return null;
    }
    const currentJob = jobs.find((x) => x.jobId === selectedJobIds);
    const isTimeClock = selectedJobIds === GLOBAL_JOB;
    const isGeneral = currentJob?.jobStatus === JobStatus.GeneralJob;

    if (isTimeClock || isGeneral) {
        return (
            <SpecialJobInfo
                currentJob={currentJob}
                isGeneral={isGeneral}
                isTimeClock={isTimeClock}
            />
        );
    }

    if (currentJob && currentJob.jobId !== ALL_JOBS) {
        return (
            <JobInfo
                currentJob={currentJob}
                permissions={permissions}
                ownerInfo={ownerInfo}
                templatesOnly={templatesOnly}
                isTemplateMode={isTemplateMode}
            />
        );
    }

    return null;
}
